import { type FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Container, Input } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { useApplicantSession } from '@/lib/applicant/useApplicantSession';
import { fieldError, type Problem, toProblem } from '@/lib/problem';
import { ApplicantLayout } from './ApplicantLayout';
import styles from './ApplicantSignInPage.module.css';

/**
 * Applicants sign in with a code sent to their email address: no password to
 * choose, forget or reuse. The first sign-in for an address starts an
 * application; later ones, on any device, return to it.
 */
export function ApplicantSignInPage() {
  const { applicant, requestCode, signIn } = useApplicantSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/apply';

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  if (applicant && !busy) {
    return <Navigate to={from} replace />;
  }

  async function send(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await requestCode(email.trim());
      setSentTo(email.trim());
      setCode('');
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    if (sentTo === null) return;
    setBusy(true);
    setProblem(null);
    try {
      await signIn(sentTo, code);
      navigate(from, { replace: true });
    } catch (error) {
      setProblem(toProblem(error));
      setBusy(false);
    }
  }

  return (
    <ApplicantLayout>
      <Container>
        <div className={styles.panel}>
          <h1 className={styles.heading}>Sign in to apply</h1>
          {sentTo === null ? (
            <>
              <p className={styles.lede}>
                Enter your email address and we will send you a six-digit code. Use the same
                address each time and your answers will be waiting, on any device.
              </p>
              {problem && Object.keys(problem.fields).length === 0 && (
                <ProblemAlert problem={problem} title="The code could not be sent" />
              )}
              <form onSubmit={(event) => void send(event)} className={styles.form} noValidate>
                <Input
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  required
                  {...fieldError(problem, 'email')}
                />
                <Button type="submit" block disabled={busy || email.trim() === ''}>
                  {busy ? 'Sending' : 'Send me a code'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Alert tone="info" title="Check your email">
                If {sentTo} can receive email, a code is on its way. It works for ten minutes.
              </Alert>
              {problem && <ProblemAlert problem={problem} title="Not signed in" />}
              <form onSubmit={(event) => void verify(event)} className={styles.form} noValidate>
                <Input
                  label="Six-digit code"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/[^\d\s]/g, ''))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={7}
                  required
                />
                <Button type="submit" block disabled={busy || code.replace(/\s/g, '').length !== 6}>
                  {busy ? 'Signing in' : 'Sign in'}
                </Button>
              </form>
              <div className={styles.secondary}>
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => void send()}>
                  Send a new code
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setSentTo(null);
                    setProblem(null);
                  }}
                >
                  Use a different address
                </Button>
              </div>
            </>
          )}
          <p className={styles.note}>
            We use your address only to sign you in and to write to you about your
            application. Members of the advisory team <Link to="/signin">sign in here</Link>.
          </p>
        </div>
      </Container>
    </ApplicantLayout>
  );
}
